const { MediaTrack, TrackInput, inertCopy, restoreFiles } = await import(
  globalThis.MeriAssets?.mediaTrack || './media-track.js'
);

// Zoom files are decoded off screen before they replace the visible picture.
// One shared task per file keeps rapid arrows and neighbour preloads from
// requesting the same file twice; the size bound keeps long sessions small.
const zoomDecodes = new Map();
function zoomSource(link) {
  return { src: link.href, srcset: link.dataset.zoomSrcset || '', sizes: link.dataset.zoomSizes || '100vw' };
}
function decodeZoom(source) {
  const key = source.srcset || source.src;
  let entry = zoomDecodes.get(key);
  if (!entry) {
    const loader = new Image();
    loader.decoding = 'async';
    if (source.srcset) {
      loader.sizes = source.sizes;
      loader.srcset = source.srcset;
    }
    loader.src = source.src;
    // A failed decode still lets the visible image try the file natively.
    entry = { loader, task: loader.decode().catch(() => {}) };
    zoomDecodes.set(key, entry);
    if (zoomDecodes.size > 48) zoomDecodes.delete(zoomDecodes.keys().next().value);
  }
  return entry.task;
}

// YouTube embeds speak a postMessage protocol (the same one YouTube's IFrame Player API wraps).
// Talking to it directly keeps every script on Shopify's servers (Theme Store requirement).
let youtubeIds = 0;
const youtubeStates = { '-1': 'ready', 0: 'ended', 1: 'playing', 2: 'paused', 3: 'buffering', 5: 'ready' };

export class GalleryYouTubePlayer {
  constructor(media, frame, view = frame.ownerDocument?.defaultView || globalThis) {
    this.media = media;
    this.host = media.querySelector('[data-gallery-video-host]');
    this.preview = media.querySelector('[data-gallery-video-preview]');
    this.frame = frame;
    this.wantsPlayback = false;
    this.id = ++youtubeIds;
    const source = new URL(frame.src, view.location?.href);
    // Prepare the selected video without playing; the explicit click owns playback.
    source.searchParams.set('autoplay', '0');
    source.searchParams.set('enablejsapi', '1');
    source.searchParams.set('playsinline', '1');
    source.searchParams.set('loop', '1');
    source.searchParams.set('playlist', source.pathname.split('/').filter(Boolean).at(-1));
    if (!source.searchParams.has('origin') && view.location?.origin)
      source.searchParams.set('origin', view.location.origin);
    this.origin = source.origin;
    frame.src = source.href;
    frame.loading = 'eager';
    frame.setAttribute('data-gallery-youtube', '');
    this.host.replaceChildren(frame);
    media.dataset.youtubeState = 'loading';
    this.events = new AbortController();
    view.addEventListener?.('message', (event) => this.receive(event), { signal: this.events.signal });
    // The player reports only after the page registers as a listener; repeat until it answers.
    this.attempts = 0;
    const listen = () => {
      clearTimeout(this.listenTimer);
      if (this.ready || this.disposed) return;
      if (this.attempts++ >= 40) {
        this.fail('YouTube player did not respond');
        return;
      }
      this.post({ event: 'listening' });
      this.listenTimer = setTimeout(listen, 250);
    };
    frame.addEventListener?.('load', listen, { signal: this.events.signal });
  }
  post(message) {
    this.frame.contentWindow?.postMessage(JSON.stringify({ ...message, id: this.id, channel: 'widget' }), this.origin);
  }
  command(func, args = []) {
    this.post({ event: 'command', func, args });
  }
  fail(message) {
    if (this.disposed) return;
    this.media.dataset.youtubeState = 'error';
    this.media.dataset.playbackError = String(message);
  }
  receive(event) {
    if (this.disposed || event.source !== this.frame.contentWindow || event.origin !== this.origin) return;
    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    if (!data || typeof data !== 'object' || (data.id !== undefined && data.id !== this.id)) return;
    if (data.event === 'onReady') {
      if (this.ready) return;
      this.ready = true;
      clearTimeout(this.listenTimer);
      for (const event of ['onStateChange', 'onError', 'onAutoplayBlocked']) this.command('addEventListener', [event]);
      this.media.dataset.youtubeState = 'ready';
      if (this.wantsPlayback) this.play();
    } else if (data.event === 'onStateChange') {
      if (data.info === 1 && !this.wantsPlayback) {
        this.command('pauseVideo');
        return;
      }
      this.media.dataset.youtubeState = youtubeStates[data.info] || 'ready';
    } else if (data.event === 'onAutoplayBlocked') this.media.dataset.youtubeState = 'blocked';
    else if (data.event === 'onError') this.fail(data.info);
  }
  play() {
    if (this.disposed) return;
    this.wantsPlayback = true;
    this.preview.hidden = true;
    this.host.hidden = false;
    if (!this.ready) return;
    this.media.dataset.youtubeState = 'play-requested';
    // For an already prepared player this runs inside the original user click.
    this.command('playVideo');
  }
  pause() {
    const wasRequested = this.wantsPlayback;
    this.wantsPlayback = false;
    if (this.ready && wasRequested) {
      this.command('pauseVideo');
      this.command('seekTo', [0, true]);
    }
    this.host.hidden = true;
    this.preview.hidden = false;
  }
  destroy() {
    this.disposed = true;
    this.wantsPlayback = false;
    clearTimeout(this.listenTimer);
    this.events.abort();
    this.frame.remove();
    this.host.hidden = true;
    this.preview.hidden = false;
    delete this.media.dataset.youtubeState;
    delete this.media.dataset.playbackError;
  }
}

export class MeriGallery extends (globalThis.HTMLElement || class {}) {
  get index() {
    return (
      this.motion?.index ??
      Math.max(0, this.slides?.findIndex((slide) => slide.dataset.mediaId === this.selectedMediaId) ?? 0)
    );
  }
  // 2.9.1 (Owner): the homepage featured product keeps the media after its first slide unloaded until the shopper's
  // first press or keyboard use (snippets/product-gallery.liquid). Once loaded, it stays loaded for the visit.
  get mediaWaiting() {
    return this.hasAttribute('data-media-defer') && !this.hasAttribute('data-media-ready');
  }
  connectedCallback() {
    const track = this.querySelector('[data-gallery-track]');
    if (this.motion?.matches(track, [...this.querySelectorAll('[data-media-id]')])) return;
    try {
      this.initialize();
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }
  initialize() {
    const selected = this.slides?.[this.index]?.dataset.mediaId || this.dataset.initialMediaId;
    this.cleanup();
    this.controller = new AbortController();
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-media-id]')];
    if (!this.track || !this.slides.length) return;
    const more = this.querySelector('[data-gallery-more]');
    if (more) more.hidden = !this.querySelector('[data-gallery-more-slides]');
    const signal = this.controller.signal;
    this.addEventListener(
      'click',
      (event) => {
        // A tap or click on the gallery is the shopper's action (the click that ends a swipe never arrives here).
        this.beginBrowsing();
        const videoButton = event.target.closest('[data-gallery-video-activate]');
        if (videoButton) {
          const media = videoButton.closest('[data-gallery-video]');
          const youtube = this.prepareYouTube(media);
          if (youtube) {
            youtube.play();
            return;
          }
          const host = media?.querySelector('[data-gallery-video-host]');
          const preview = media?.querySelector('[data-gallery-video-preview]');
          const template = media?.querySelector('[data-gallery-video-template]');
          if (host && preview && (host.querySelector('video') || template)) {
            if (!host.querySelector('video')) host.replaceChildren(template.content.cloneNode(true));
            preview.hidden = true;
            host.hidden = false;
            const video = host.querySelector('video');
            video?.play()?.catch((error) => {
              if (video.isConnected && !host.hidden) video.dataset.playbackError = error.name || 'PlaybackError';
            });
          }
          return;
        }
        if (event.target.closest('[data-gallery-more]')) {
          const firstNew = this.slides.length;
          const extra = this.querySelectorAll('[data-gallery-more-slides]');
          if (!extra.length) return;
          this.input?.dispose();
          this.motion?.dispose();
          this.motion = null;
          extra.forEach((template) => {
            this.track.append(template.content.cloneNode(true));
            template.remove();
          });
          this.querySelectorAll('[data-gallery-more-thumbnails]').forEach((template) => {
            template.parentElement.append(template.content.cloneNode(true));
            template.remove();
          });
          more?.remove();
          this.connectedCallback();
          this.show(firstNew, { instant: true });
          (this.querySelector(`[data-gallery-index="${firstNew}"]`) || this.track).focus({ preventScroll: true });
          return;
        }
        const thumb = event.target.closest('[data-gallery-index], [data-gallery-copy]');
        if (thumb) this.show(Number(thumb.dataset.galleryIndex ?? thumb.dataset.galleryCopy));
        if (event.target.closest('[data-gallery-prev]')) this.navigate(-1);
        if (event.target.closest('[data-gallery-next]')) this.navigate(1);
        if (event.target.closest('[data-gallery-thumb-prev]')) this.scrollThumbnails(-1);
        if (event.target.closest('[data-gallery-thumb-next]')) this.scrollThumbnails(1);
        const zoom = event.target.closest('[data-zoom-link]');
        const dialog = this.querySelector('dialog');
        if (zoom && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && dialog?.showModal) {
          event.preventDefault();
          this.zoomLinks = [...this.querySelectorAll('[data-zoom-link]')];
          this.showZoom(this.zoomLinks.indexOf(zoom));
          if (!dialog.open) {
            dialog.showModal();
            // Focus the dialog itself so its name is announced first (Shopify accessibility guidance).
            dialog.focus({ preventScroll: true });
          }
        }
        if (event.target.closest('[data-zoom-prev]')) this.showZoom(this.zoomIndex - 1);
        if (event.target.closest('[data-zoom-next]')) this.showZoom(this.zoomIndex + 1);
        if (event.target.closest('[data-zoom-close]')) dialog?.close();
      },
      { signal },
    );
    // A zoom file that finishes decoding after close must not replace the picture.
    this.querySelector('dialog')?.addEventListener('close', () => this.cancelZoom(), { signal });
    this.querySelector('dialog')?.addEventListener(
      'keydown',
      (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        this.showZoom(this.zoomIndex + (event.key === 'ArrowRight' ? direction : -direction));
      },
      { signal },
    );
    this.track.addEventListener(
      'keydown',
      (event) => {
        if (event.target !== this.track) return;
        const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        const destination = {
          ArrowRight: this.index + direction,
          ArrowLeft: this.index - direction,
          Home: 0,
          End: this.slides.length - 1,
        }[event.key];
        if (destination !== undefined) {
          event.preventDefault();
          this.show(destination);
        }
      },
      { signal },
    );
    this.motion = new MediaTrack(this.track, this.slides, {
      railClass: 'gallery-rail',
      railAttribute: 'data-gallery-rail',
      duration: Number.isFinite(Number(this.dataset.speed)) ? Number(this.dataset.speed) : 350,
      reduced: () => matchMedia('(prefers-reduced-motion: reduce)').matches,
      onIndex: (index) => this.activate(index),
      // A model next to the current slide prepares only after the slide has settled (product-model.js).
      onBusy: (busy) => {
        this.toggleAttribute('data-gallery-moving', busy);
        if (!busy) this.dispatchEvent(new CustomEvent('meri:gallery:settle'));
      },
      preview: (slide) => {
        const copy = document.createElement('div');
        copy.className = 'gallery-slide';
        copy.setAttribute('data-gallery-preview', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        const image = slide.querySelector('img');
        // A copy of a slide that still waits for the shopper waits with it, built without its file (media-track.js
        // inertCopy); loadDeferredMedia starts both.
        const waiting = this.mediaWaiting && slide.hasAttribute('data-media-deferred');
        if (waiting) copy.setAttribute('data-media-deferred', '');
        if (image) {
          const visual = waiting ? inertCopy(image) : image.cloneNode(true);
          visual.removeAttribute('id');
          visual.loading = waiting ? 'lazy' : 'eager';
          // The wrap-around preview must not compete with the LCP image.
          visual.fetchPriority = 'low';
          visual.decoding = 'async';
          copy.append(visual);
        }
        return copy;
      },
    });
    this.setAttribute('data-gallery-enhanced', '');
    this.thumbnailStrip = this.querySelector('.gallery-thumbnails');
    this.setupThumbnails(signal);
    this.input = new TrackInput(this.track, this.motion, {
      allowVideo: true,
      onNavigate: (direction) => this.navigate(direction, true),
    });
    this.visibilityObserver = new IntersectionObserver((entries) => {
      // Use the latest of any batched entries so a stale intersecting entry cannot win.
      const entry = entries.at(-1);
      if (!signal.aborted && !entry.isIntersecting) this.slides.forEach((slide) => this.pauseMedia(slide));
    });
    this.visibilityObserver.observe(this.track);
    window.addEventListener('pagehide', () => this.slides.forEach((slide) => this.pauseMedia(slide)), { signal });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.slides.forEach((slide) => this.pauseMedia(slide));
      },
      { signal },
    );
    this.show(
      Math.max(
        0,
        this.slides.findIndex((slide) => slide.dataset.mediaId === selected),
      ),
      { instant: true },
    );
    // The neighbours start after the page's own load, so the first picture keeps the network, and then at idle.
    // A homepage gallery still waiting for the shopper skips this; its first press or key starts everything.
    const ready = () => {
      if (signal.aborted || this.mediaWaiting) return;
      this.warmReady = true;
      this.warmAround();
    };
    const idle = () =>
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback(ready, { timeout: 2000 })
        : setTimeout(ready, 200);
    if (this.warmReady || document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true, signal });
    // 2.9.9 (Owner): a quick view opens because the shopper is browsing, so its pictures start at once; a gallery
    // rebuilt after the shopper began (the slides beyond the display limit shown) carries on.
    if (this.browsing || this.hasAttribute('data-gallery-browsing')) this.loadAllMedia();
    // A finger on the gallery, or a pointer or focus on an arrow or thumbnail, names the next slide before the
    // move, so its picture starts at once even before the page has finished loading.
    const intent = (event) => {
      // 2.9.1 (Owner): a press (mouse or pen, before any move) or keyboard focus is the shopper's first action; a
      // pointer only resting on an arrow is not, so a waiting homepage gallery loads nothing yet. 2.9.1 (Owner, second
      // round): a finger on the gallery may only be scrolling the page, so touch counts as a tap (the click) or a
      // sideways swipe (navigate), never on contact.
      const action = event.type !== 'pointerover' && event.pointerType !== 'touch';
      if (action) this.beginBrowsing();
      else if (this.mediaWaiting) return;
      const count = this.slides.length;
      const thumb = event.target.closest?.('[data-gallery-index], [data-gallery-copy]');
      if (thumb) this.warmSlide(this.slides[Number(thumb.dataset.galleryIndex ?? thumb.dataset.galleryCopy)]);
      if (event.target.closest?.('[data-gallery-next]')) this.warmSlide(this.slides[(this.index + 1) % count]);
      if (event.target.closest?.('[data-gallery-prev]')) this.warmSlide(this.slides[(this.index - 1 + count) % count]);
      if (event.type === 'pointerdown' && event.target.closest?.('[data-gallery-track]')) {
        this.warmSlide(this.slides[(this.index + 1) % count]);
        this.warmSlide(this.slides[(this.index - 1 + count) % count]);
      }
    };
    for (const type of ['pointerover', 'pointerdown', 'focusin'])
      this.addEventListener(type, intent, { signal, passive: true });
    this.resizeObserver = new ResizeObserver(() => {
      if (signal.aborted) return;
      const width = this.track.getBoundingClientRect().width;
      if (width !== this.motion.width && this.input.pointer) this.input.finish(null, true);
      this.motion.resize(width);
      if (this.sizeThumbnails()) this.revealThumbnail();
    });
    this.resizeObserver.observe(this.track);
    if (this.thumbnailStrip) this.resizeObserver.observe(this);
    const section = this.closest('.shopify-section');
    section?.addEventListener('shopify:section:unload', () => this.cleanup(), { signal });
    section?.addEventListener('shopify:section:load', () => this.connectedCallback(), { signal });
    section?.addEventListener('shopify:section:reorder', () => this.connectedCallback(), { signal });
  }
  show(index, { instant = false } = {}) {
    if (!this.motion || !Number.isInteger(index)) return;
    if (this.input?.pointer) this.input.finish(null, true);
    if (index !== this.index) this.pauseMedia(this.slides[this.index]);
    this.motion.goTo(index, instant);
  }
  navigate(direction, gesture = false) {
    // An arrow, a key or a sideways swipe moves the gallery: that is the shopper's action.
    this.beginBrowsing();
    if (!gesture && this.input?.pointer) this.input.finish(null, true);
    this.pauseMedia(this.slides[this.index]);
    this.motion.move(direction);
  }
  cancelZoom() {
    this.zoomGeneration = (this.zoomGeneration || 0) + 1;
    clearTimeout(this.zoomFallback);
    this.zoomFallback = null;
  }
  showZoom(index) {
    if (!this.zoomLinks?.length) return;
    this.cancelZoom();
    const count = this.zoomLinks.length;
    const zoomIndex = (index + count) % count;
    this.zoomIndex = zoomIndex;
    const generation = this.zoomGeneration;
    const link = this.zoomLinks[zoomIndex],
      image = this.querySelector('[data-zoom-image]');
    const counter = this.querySelector('[data-zoom-counter]');
    for (const button of this.querySelectorAll('[data-zoom-prev],[data-zoom-next]')) button.hidden = count < 2;
    // Picture, alt text and counter always change in the same frame, so the
    // counter never names a picture that is not on screen yet.
    const present = (source) => {
      if (generation !== this.zoomGeneration) return;
      image.sizes = source.sizes;
      image.srcset = source.srcset;
      image.src = source.src;
      image.alt = link.querySelector('img')?.alt || '';
      if (counter) counter.textContent = `${zoomIndex + 1} / ${count}`;
    };
    // The gallery picture is already decoded at a smaller width: show it at
    // once, then exchange it for the sharper file after that file is decoded.
    const preview = link.querySelector('img');
    const previewSource = preview && { src: preview.currentSrc || preview.src, srcset: '', sizes: '' };
    if (preview?.complete && preview.naturalWidth) present(previewSource);
    const full = zoomSource(link);
    // Decoding can stall (slow network, or a hidden tab where browsers defer decoding). Never leave the
    // counter on the previous picture for long: after a short wait show the light gallery picture (or the
    // full file) and let the sharp file replace it once decoded.
    const fallback = setTimeout(() => present(previewSource?.src ? previewSource : full), 400);
    this.zoomFallback = fallback;
    decodeZoom(full).then(() => {
      clearTimeout(fallback);
      present(full);
    });
    if (count > 1)
      for (const offset of [-1, 1]) decodeZoom(zoomSource(this.zoomLinks[(zoomIndex + offset + count) % count]));
  }
  showMedia(id) {
    const index = this.slides?.findIndex((slide) => slide.dataset.mediaId === String(id));
    if (!(index >= 0)) return false;
    // A variant choice shows its media: that is the shopper's action.
    this.beginBrowsing();
    this.show(index);
    return true;
  }
  setupThumbnails(signal) {
    const strip = this.thumbnailStrip;
    if (!strip) return;
    this.thumbnails = [...strip.querySelectorAll('[data-gallery-index]')];
    this.sizeThumbnails();
    const nativeScrollEnd = 'onscrollend' in strip;
    let scrolling = false;
    strip.addEventListener(
      'scroll',
      () => {
        scrolling = true;
        clearTimeout(this.thumbnailSettle);
        // Native touch and trackpad scrolling keep their momentum. Rebase only
        // after scrollend; a gap between frames is not the end of a fling.
        if (!nativeScrollEnd) this.thumbnailSettle = setTimeout(() => this.settleThumbnails(), 140);
      },
      { passive: true, signal },
    );
    const release = () => {
      this.thumbnailPointer = false;
      clearTimeout(this.thumbnailSettle);
      if (!nativeScrollEnd) this.thumbnailSettle = setTimeout(() => this.settleThumbnails(), 180);
      else if (!scrolling) this.settleThumbnails();
    };
    strip.addEventListener(
      'pointerdown',
      () => {
        this.thumbnailPointer = true;
      },
      { passive: true, signal },
    );
    for (const type of ['pointerup', 'pointercancel'])
      window.addEventListener(
        type,
        () => {
          if (this.thumbnailPointer) release();
        },
        { passive: true, signal },
      );
    strip.addEventListener(
      'touchstart',
      () => {
        this.thumbnailTouch = true;
      },
      { passive: true, signal },
    );
    for (const type of ['touchend', 'touchcancel'])
      strip.addEventListener(
        type,
        (event) => {
          this.thumbnailTouch = event.touches.length > 0;
          release();
        },
        { passive: true, signal },
      );
    for (const type of ['pointerdown', 'wheel'])
      strip.addEventListener(
        type,
        () => {
          this.thumbnailTarget = null;
        },
        { passive: true, signal },
      );
    strip.addEventListener(
      'scrollend',
      () => {
        scrolling = false;
        this.settleThumbnails();
      },
      { signal },
    );
    strip.addEventListener(
      'focusin',
      (event) => {
        const button = event.target.closest('[data-gallery-index]');
        if (button?.matches(':focus-visible') && !this.thumbnailPointer && !this.thumbnailTouch)
          this.revealThumbnail(Number(button.dataset.galleryIndex));
      },
      { signal },
    );
  }
  sizeThumbnails() {
    const strip = this.thumbnailStrip;
    if (!strip || !this.thumbnails?.length || !strip.clientWidth) return;
    const style = getComputedStyle(strip);
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    const width = this.clientWidth - padding;
    if (width <= 0) return;
    // 2.9.4: the merchant's thumbnail size comes in rem and grows with a wide screen like the rest of the page.
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const thumb = style.getPropertyValue('--gallery-thumb').trim();
    const requested = parseFloat(thumb) * (thumb.endsWith('rem') ? rem : 1);
    const desired = Math.max(3 * rem, requested || 4.875 * rem);
    const gap = parseFloat(style.columnGap) || 10;
    const columns = Math.min(this.thumbnails.length, Math.max(1, Math.floor((width + gap) / (desired + gap))));
    const size = Number.isFinite(requested) ? Math.min(desired, width) : (width - (columns - 1) * gap) / columns;
    const loop = columns < this.thumbnails.length;
    if (
      this.thumbnailWidth === width &&
      this.thumbnailColumns === columns &&
      this.thumbnailStep === size + gap &&
      this.thumbnailLoop === loop
    )
      return;
    this.thumbnailWidth = width;
    this.thumbnailColumns = columns;
    this.thumbnailStep = size + gap;
    this.thumbnailCycle = this.thumbnails.length * this.thumbnailStep;
    this.thumbnailLoop = loop;
    this.style.setProperty('--gallery-strip-width', `${columns * size + (columns - 1) * gap + padding}px`);
    strip.style.setProperty('--gallery-thumb-visible', size + 'px');
    // Loop copies depend only on the thumbnail set and whether it loops. A width
    // change just resizes them through the custom properties above; rebuilding
    // 4N nodes on every resize frame only churned the DOM and layout.
    const existing = strip.querySelectorAll('[data-gallery-copy]');
    const current = loop && existing.length === 4 * this.thumbnails.length && this.thumbnailCopySet === this.thumbnails;
    if (!current) existing.forEach((node) => node.remove());
    this.thumbnailCopySet = loop ? this.thumbnails : null;
    if (loop && !current) {
      const copies = () => {
        const fragment = document.createDocumentFragment();
        for (let cycle = 0; cycle < 2; cycle++)
          for (const original of this.thumbnails) {
            const copy = original.cloneNode(true);
            copy.dataset.galleryCopy = original.dataset.galleryIndex;
            copy.removeAttribute('data-gallery-index');
            copy.removeAttribute('id');
            copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
            copy.tabIndex = -1;
            copy.setAttribute('aria-hidden', 'true');
            fragment.append(copy);
          }
        return fragment;
      };
      strip.prepend(copies());
      strip.append(copies());
    }
    for (const button of this.querySelectorAll('[data-gallery-thumb-prev], [data-gallery-thumb-next]'))
      button.disabled = !loop;
    this.setThumbnailOffset(loop ? 2 * this.thumbnailCycle : 0);
    return true;
  }
  thumbnailOffset() {
    return Math.abs(this.thumbnailStrip?.scrollLeft || 0);
  }
  setThumbnailOffset(offset, behavior = 'instant') {
    const strip = this.thumbnailStrip;
    if (!strip) return;
    const sign = getComputedStyle(strip).direction === 'rtl' ? -1 : 1;
    strip.scrollTo({ left: offset * sign, behavior });
  }
  settleThumbnails() {
    clearTimeout(this.thumbnailSettle);
    if (!this.thumbnailLoop || !this.thumbnailStrip?.isConnected || this.thumbnailPointer || this.thumbnailTouch)
      return;
    // A cancelled smooth scroll may emit scrollend after a newer click.
    if (this.thumbnailTarget != null && Math.abs(this.thumbnailOffset() - this.thumbnailTarget) > 1) return;
    const count = this.thumbnails.length;
    const item = Math.round(this.thumbnailOffset() / this.thumbnailStep);
    const central = (2 * count + (((item % count) + count) % count)) * this.thumbnailStep;
    this.thumbnailTarget = null;
    if (Math.abs(this.thumbnailOffset() - central) > 1) this.setThumbnailOffset(central);
  }
  scrollThumbnails(direction) {
    if (!this.thumbnailLoop) return;
    clearTimeout(this.thumbnailSettle);
    const current = this.thumbnailTarget ?? this.thumbnailOffset();
    let target = Math.round(current / this.thumbnailStep) * this.thumbnailStep + direction * this.thumbnailStep;
    const cycle = this.thumbnailCycle;
    if (target < cycle || target >= 4 * cycle) {
      const offset = 2 * cycle + (((this.thumbnailOffset() % cycle) + cycle) % cycle);
      target = 2 * cycle + (((target % cycle) + cycle) % cycle);
      if (direction > 0 && target < offset) target += cycle;
      if (direction < 0 && target > offset) target -= cycle;
      this.setThumbnailOffset(offset);
    }
    this.setThumbnailOffset(target, matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
    this.thumbnailTarget = target;
  }
  revealThumbnail(index = this.index) {
    const strip = this.thumbnailStrip;
    if (!strip || !this.thumbnailLoop) return;
    clearTimeout(this.thumbnailSettle);
    const step = this.thumbnailStep;
    const center = this.thumbnailOffset() + ((this.thumbnailColumns - 1) * step) / 2;
    const first = index * step + Math.round((center - index * step) / this.thumbnailCycle) * this.thumbnailCycle;
    const offset = Math.round(this.thumbnailOffset() / step) * step;
    const last = offset + (this.thumbnailColumns - 1) * step;
    const target = first < offset ? first : first > last ? first - (this.thumbnailColumns - 1) * step : offset;
    this.thumbnailTarget = null;
    this.setThumbnailOffset(target);
  }
  // The shopper's first press, tap, swipe, key or variant choice on the gallery.
  beginBrowsing() {
    if (this.mediaWaiting) this.loadDeferredMedia();
    else this.loadAllMedia();
  }
  // 2.9.1 (Owner): the shopper's first action on a homepage gallery (a press, tap, swipe or keyboard use) starts every
  // picture, the ones on both sides of the current slide first, then those of the slides beyond the display limit
  // (loadAllMedia). Videos load their cover and basic information (warmSlide). The gallery's 3D files download then,
  // or once the page has loaded if that comes first, and a model builds only when the shopper opens it
  // (product-model.js, 2.9.1 second round).
  loadDeferredMedia() {
    if (!this.mediaWaiting || !this.slides?.length) return;
    this.setAttribute('data-media-ready', '');
    // The wrap-around copies (media-track.js inertCopy) get their files back. They show the first and last slides,
    // which load first, so they share those files.
    for (const image of this.querySelectorAll('[data-gallery-preview][data-media-deferred] img')) {
      image.loading = 'eager';
      image.fetchPriority = 'low';
    }
    restoreFiles(this);
    if (
      [...this.querySelectorAll('[data-gallery-more-slides]')].some((template) =>
        template.content.querySelector('meri-model-media'),
      )
    )
      import(globalThis.MeriAssets?.productModel || './product-model.js')
        .then(({ prepareDeferredModels }) => {
          if (this.isConnected && !this.controller?.signal.aborted) prepareDeferredModels(this);
        })
        .catch(() => {});
    this.warmReady = true;
    this.warmAround();
    this.loadAllMedia();
  }
  // 2.9.9 (Owner): once the shopper browses a gallery (a quick view opening, or the first press, tap, swipe, key or
  // variant choice on a product page or the homepage featured product), every picture loads from the current slide
  // outward: the slides on both sides first, then the nearest of the rest, two at a time, in the order of the slide
  // the shopper is on at that moment; the pictures of the slides beyond the display limit come last. Started all at
  // once, the pictures split a phone connection between them and even the next slide stayed blank (2026-10-26, 1.6
  // Mbps). A video slide loads its cover here; warmSlide prepares the players beside the current slide. A model's
  // preview is what its slide shows until the shopper opens the model (2.9.1, second round), so it loads like any
  // picture.
  loadAllMedia() {
    if (!this.slides?.length) return;
    this.browsing = true;
    if (!this.mediaQueue) {
      this.mediaQueue = new Set(this.slides);
      this.mediaLoading = 0;
      this.extraPictures = [...this.querySelectorAll('[data-gallery-more-slides]')].flatMap((template) => [
        ...template.content.querySelectorAll('img'),
      ]);
    }
    this.loadNextMedia();
  }
  loadNextMedia() {
    const signal = this.controller?.signal;
    if (!this.mediaQueue || !signal || signal.aborted) return;
    const count = this.slides.length;
    // Steps from the current slide; at the same distance the slide ahead comes before the one behind.
    const place = (slide) => {
      const ahead = (this.slides.indexOf(slide) - this.index + count) % count;
      const behind = (count - ahead) % count;
      return ahead <= behind ? [ahead, 0] : [behind, 1];
    };
    const sooner = (a, b) => {
      const [first, second] = [place(a), place(b)];
      return first[0] - second[0] || first[1] - second[1];
    };
    while (this.mediaLoading < 2) {
      let pictures = [];
      let beside = false;
      if (this.mediaQueue.size) {
        const slide = [...this.mediaQueue].sort(sooner)[0];
        this.mediaQueue.delete(slide);
        beside = place(slide)[0] <= 1;
        // A picture that has loaded or failed is done; one that never started reports neither.
        pictures = [...slide.querySelectorAll('img')].filter((image) => !image.complete);
      } else if (this.extraPictures?.length) {
        // A slide beyond the display limit is not on the page yet: its file downloads for when it is shown.
        const source = this.extraPictures.shift();
        const preload = new Image();
        preload.fetchPriority = 'low';
        preload.sizes = source.getAttribute('sizes') || '';
        if (source.hasAttribute('srcset')) preload.srcset = source.getAttribute('srcset');
        if (source.hasAttribute('src')) preload.src = source.getAttribute('src');
        pictures = [preload];
      } else return;
      if (!pictures.length) continue;
      this.mediaLoading += 1;
      let waiting = pictures.length;
      const done = () => {
        if (--waiting) return;
        this.mediaLoading -= 1;
        this.loadNextMedia();
      };
      for (const picture of pictures) {
        picture.addEventListener('load', done, { once: true, signal });
        picture.addEventListener('error', done, { once: true, signal });
        if (!picture.isConnected) continue;
        picture.fetchPriority = beside ? 'high' : 'low';
        picture.loading = 'eager';
      }
    }
  }
  // 2.8.6 (Owner): pictures and videos next to the current slide load before the customer reaches them, so a slide
  // shows its picture at once. The slides wait outside the clipped viewport, where the browser's lazy loading would
  // start a picture only as it slides in.
  warmAround(index = this.index) {
    if (!this.warmReady || !this.slides?.length) return;
    const count = this.slides.length;
    for (const offset of [-1, 1]) this.warmSlide(this.slides[(index + offset + count) % count]);
  }
  warmSlide(slide) {
    if (!slide || slide.hasAttribute('data-gallery-warm')) return;
    slide.setAttribute('data-gallery-warm', '');
    // A model's preview is what its slide shows until the shopper opens the model (2.9.1, second round), so it is
    // fetched and decoded ahead like any picture.
    for (const image of slide.querySelectorAll('img')) {
      if (image.loading === 'lazy') image.loading = 'eager';
      image.decode?.().catch(() => {});
    }
    // 2.9.1 (Owner): on the homepage a video next to the slide loads its cover and basic information only; the film
    // downloads when the shopper plays it, and a YouTube player is built for its own slide or the Play click.
    const homepage = this.hasAttribute('data-media-defer');
    slide.querySelectorAll('[data-gallery-video]').forEach((media) => {
      // A YouTube player prepares paused; it plays only from the customer's click.
      if (!homepage && this.prepareYouTube(media)) return;
      // A Shopify-hosted video buffers its start behind the cover. Other players start with sound, so they wait.
      const host = media.querySelector('[data-gallery-hosted-video]');
      const template = media.querySelector('[data-gallery-video-template]');
      if (!host || host.querySelector('video') || !template?.content.querySelector('video')) return;
      const player = template.content.cloneNode(true);
      player.querySelector('video').preload = homepage ? 'metadata' : 'auto';
      host.replaceChildren(player);
    });
  }
  prepareYouTube(media) {
    if (!media) return null;
    this.youtubePlayers ||= new Map();
    if (this.youtubePlayers.has(media)) return this.youtubePlayers.get(media);
    const frame = media.querySelector('[data-gallery-video-template]')?.content.querySelector('iframe');
    if (!frame || !/^https:\/\/(www\.)?youtube(-nocookie)?\.com\/embed\//.test(frame.src)) return null;
    const player = new GalleryYouTubePlayer(media, frame.cloneNode(true));
    this.youtubePlayers.set(media, player);
    return player;
  }
  pauseMedia(slide) {
    slide.querySelectorAll('[data-gallery-video]').forEach((media) => {
      const youtube = this.youtubePlayers?.get(media);
      if (youtube) {
        youtube.pause();
        return;
      }
      const host = media.querySelector('[data-gallery-video-host]');
      const preview = media.querySelector('[data-gallery-video-preview]');
      const video = host?.querySelector('video');
      video?.pause();
      if (video?.readyState) video.currentTime = 0;
      if (host && !host.hasAttribute('data-gallery-hosted-video')) host.replaceChildren();
      if (host) host.hidden = true;
      if (preview) preview.hidden = false;
    });
    slide.querySelectorAll('video').forEach((video) => video.pause());
    slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
    slide.querySelectorAll('iframe').forEach((frame) => {
      if (frame.hasAttribute('data-gallery-youtube')) return;
      const source = frame.getAttribute('src');
      if (source && source !== 'about:blank') {
        frame.dataset.mediaSrc = source;
        frame.setAttribute('src', 'about:blank');
      }
    });
  }
  activate(index) {
    if (!this.slides?.[index]) return;
    this.selectedMediaId = this.slides[index].dataset.mediaId;
    if (this.dataset.imageMode === 'natural') {
      const ratio = Number(this.slides[index].dataset.mediaRatio);
      if (Number.isFinite(ratio) && ratio > 0) this.style.setProperty('--gallery-ratio', ratio);
    }
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = index + 1 + ' / ' + this.slides.length;
    const previous = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    for (const button of [previous, next]) {
      if (!button) continue;
      button.disabled = this.slides.length < 2;
      button.hidden = this.slides.length < 2;
    }
    this.querySelectorAll('[data-gallery-index], [data-gallery-copy]').forEach((button) =>
      button.setAttribute(
        'aria-current',
        String(Number(button.dataset.galleryIndex ?? button.dataset.galleryCopy) === index),
      ),
    );
    this.revealThumbnail();
    const lastActive = this.activeIndex;
    this.activeIndex = index;
    // The customer has moved through the gallery (not the first selection); models next to them may prepare.
    if (lastActive != null && lastActive !== index) this.toggleAttribute('data-gallery-browsed', true);
    this.slides.forEach((slide, i) => {
      slide.inert = i !== index;
      slide.setAttribute('aria-hidden', String(i !== index));
      // Only the previously active slide can hold playing media. The first
      // activation after (re)initialization still unloads every inactive slide.
      if (i !== index) {
        if (lastActive == null || i === lastActive) this.pauseMedia(slide);
      } else {
        // A homepage gallery builds no YouTube player before the shopper's first action (the Play click builds it).
        if (!this.mediaWaiting)
          slide.querySelectorAll('[data-gallery-video]').forEach((media) => this.prepareYouTube(media));
        slide.querySelectorAll('iframe').forEach((frame) => {
          if (frame.dataset.mediaSrc && frame.getAttribute('src') === 'about:blank')
            frame.setAttribute('src', frame.dataset.mediaSrc);
        });
      }
    });
    this.dispatchEvent(
      new CustomEvent('meri:gallery:change', { detail: { mediaId: this.slides[index].dataset.mediaId } }),
    );
    this.warmAround(index);
    // The next free place goes to the picture nearest the new slide.
    this.loadNextMedia();
  }
  cleanup() {
    // Abort before removing the close listener: delayed decodes must not outlive this instance.
    this.cancelZoom();
    this.activeIndex = null;
    clearTimeout(this.thumbnailSettle);
    this.thumbnailStrip?.querySelectorAll('[data-gallery-copy]').forEach((node) => node.remove());
    this.thumbnailStrip?.style.removeProperty('--gallery-thumb-visible');
    this.style.removeProperty('--gallery-strip-width');
    this.thumbnailPointer = false;
    this.thumbnailTouch = false;
    this.thumbnailWidth = null;
    this.thumbnailCopySet = null;
    this.thumbnailTarget = null;
    this.youtubePlayers?.forEach((player) => player.destroy());
    this.youtubePlayers?.clear();
    this.input?.dispose();
    this.input = null;
    this.motion?.dispose();
    this.motion = null;
    this.controller?.abort();
    // A rebuilt gallery queues its own slides again (initialize) if the shopper had begun browsing.
    this.mediaQueue = null;
    this.mediaLoading = 0;
    this.extraPictures = null;
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.removeAttribute('data-gallery-enhanced');
    this.removeAttribute('data-gallery-moving');
    this.slides?.forEach((slide) => {
      this.pauseMedia(slide);
      slide.inert = false;
      slide.removeAttribute('aria-hidden');
      // Destroyed players prepare again for the next instance.
      slide.removeAttribute('data-gallery-warm');
    });
    this.querySelector('dialog')?.close();
  }
  disconnectedCallback() {
    this.cleanup();
  }
}
if (globalThis.customElements && !customElements.get('meri-gallery'))
  customElements.define('meri-gallery', MeriGallery);
